import React, { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { useStore } from '@tanstack/react-form';
import { TradeType, CollateralType } from '@/types';
import { DecimalInput } from '@/components/ui/DecimalInput';
import { CashPoolField } from './CashPoolField';
import { poolsItsCash } from '@/utils/trades/cash-pool-rate';
import type { TradeForm, SearchInventoryItem, CalculatedValues } from './types';
import { inputClassName, labelClassName, readOnlyInputClassName, isAmendLocked, GROSS_PRICE_FIELD_MARKER } from './types';

// LR-578: in CREATE the field is prefilled from the canonical COB T-1 close
// (same source as the blotter's "COB Gross Price"), so the label may claim COB.
// In AMEND the field shows the BOOKED price — the label must not claim COB T-1.
// Exported so the label tests pin the exact strings instead of hand-copying them.
export const GROSS_PRICE_LABEL_CREATE = 'Gross Price (COB T-1)';
export const GROSS_PRICE_LABEL_AMEND = 'Gross Price';
export const GROSS_PRICE_TITLE_CREATE = 'Gross Price from Close of Business T-1';
export const GROSS_PRICE_TITLE_AMEND = 'Booked gross price';

const formatBpsAsPercentString = (bps: number | undefined | null): string => {
  if (bps === undefined || bps === null) return '0';
  return (bps / 100).toString();
};

const isIntermediateNumericInput = (value: string): boolean => {
  return value === '' || value === '-' || value === '-0' || value.endsWith('.');
};

export interface MarginInputInnerProps {
  field: { state: { value: number | undefined }; handleChange: (v: number) => void };
  selectedInventoryItem: SearchInventoryItem | null;
  amendMode?: boolean;
}

export const MarginInputInner: React.FC<MarginInputInnerProps> = ({
  field,
  selectedInventoryItem,
  amendMode,
}) => {
  const bpsValue = field.state.value;
  // LR-495: in CREATE the input is disabled until a security is picked; in amend
  // (amendMode) editability comes from the registry via isAmendLocked, not from a
  // synthetic selectedInventoryItem.
  const inputsDisabled = !amendMode && !selectedInventoryItem;
  const [inputValue, setInputValue] = useState<string>(() => formatBpsAsPercentString(bpsValue));

  // Sync local string state when bps changes externally (prefill from inventory,
  // form reset). Preserve only mid-entry states that would be destroyed by sync
  // ('-', '-0', trailing '.'). Empty '' must not block sync — otherwise a
  // prefill that arrives while the field is cleared stays hidden.
  useEffect(() => {
    if (inputValue === '-' || inputValue === '-0' || inputValue.endsWith('.')) return;
    const localAsNum = parseFloat(inputValue);
    const localAsBps = isNaN(localAsNum) ? 0 : Math.round(localAsNum * 100);
    if (localAsBps === (bpsValue ?? 0)) return;
    setInputValue(formatBpsAsPercentString(bpsValue));
    // Intentionally omitting inputValue from deps: this effect syncs FROM bpsValue TO
    // inputValue, not the other way around. Adding inputValue would cause a feedback loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bpsValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let valStr = e.target.value;
    if (!(valStr === '' || valStr === '-' || /^-?\d*\.?\d{0,2}$/.test(valStr))) {
      return;
    }
    if (valStr.length > 1 && valStr.startsWith('0') && valStr[1] !== '.') {
      valStr = valStr.replace(/^0+/, '');
      if (valStr === '' || valStr.startsWith('.')) valStr = '0' + valStr;
    }
    if (valStr.length > 2 && valStr.startsWith('-0') && valStr[2] !== '.') {
      valStr = '-' + valStr.substring(2).replace(/^0+/, '');
      if (valStr === '-' || valStr.startsWith('-.')) valStr = '-0' + valStr.substring(1);
    }

    setInputValue(valStr);

    if (isIntermediateNumericInput(valStr)) {
      return;
    }
    const parsed = parseFloat(valStr);
    if (!isNaN(parsed)) {
      field.handleChange(Math.round(parsed * 100));
    }
  };

  const handleBlur = () => {
    if (isIntermediateNumericInput(inputValue)) {
      setInputValue(formatBpsAsPercentString(bpsValue));
    }
  };

  // Backend accepts margin_bps in [100, 19900] → margin must be in [1, 199]
  // (inclusive). Outside that range, backend rejects on submit; show inline
  // yellow warning so user catches it pre-submit.
  const isMarginValueOutOfRange =
    bpsValue !== undefined &&
    bpsValue !== null &&
    (bpsValue < 100 || bpsValue > 19900);

  return (
    <>
      <label className={labelClassName}>Margin %</label>
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={(e) => e.target.select()}
          disabled={inputsDisabled || isAmendLocked(amendMode, 'margin_bps')}
          placeholder="e.g., 110"
          title="Margin: total collateral as % of loan value"
          className={clsx(
            inputClassName,
            'pr-7',
            inputsDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed',
            isMarginValueOutOfRange && 'border-yellow-500'
          )}
        />
        <span
          className={clsx(
            'pointer-events-none absolute inset-y-0 right-2 flex items-center text-sm text-gray-500',
            inputsDisabled && 'opacity-50'
          )}
        >
          %
        </span>
      </div>
      {isMarginValueOutOfRange && (
        <p className="mt-1 text-xs text-yellow-600">
          Margin must be between 1% and 199% (inclusive).
        </p>
      )}
    </>
  );
};

interface PositionDetailsSectionProps {
  form: TradeForm;
  isSubmitted: boolean;
  selectedInventoryItem: SearchInventoryItem | null;
  /** Security currency for the read-only display cells. In create it tracks the
   *  picked inventory item; in amend it is the booked position currency (LR-495). */
  securityCurrency: string;
  calculatedValues: CalculatedValues;
  availableTradeTypes: readonly TradeType[];
  tradeTypeLabels: Record<string, string>;
  amendMode?: boolean;
}

export const PositionDetailsSection: React.FC<PositionDetailsSectionProps> = ({
  form,
  isSubmitted,
  selectedInventoryItem,
  securityCurrency,
  calculatedValues,
  availableTradeTypes,
  tradeTypeLabels,
  amendMode,
}) => {
  // LR-495: create disables inputs until a security is picked; amend governs
  // editability via the registry (isAmendLocked). Display currency comes from the
  // picked item in create, from the booked position currency in amend.
  const inputsDisabled = !amendMode && !selectedInventoryItem;
  const displayCurrency = amendMode ? securityCurrency : selectedInventoryItem?.currency;
  // LR-586: the cash pool field only exists for cash collateral, so this has to
  // be a live subscription rather than a read of the initial values.
  const collateralType = useStore(form.store, (state) => state.values.collateral_type);
  // LR-631: only the modes that actually pool their cash show the field - GMSLA
  // fee, and GMRA (which books rebate-only, LR-617, and keeps its pool).
  const rateEntryMode = useStore(form.store, (state) => state.values.rate_entry_mode);
  const tradeType = useStore(form.store, (state) => state.values.trade_type);
  const showsPool = poolsItsCash(tradeType, rateEntryMode);
  // LR-844: a repo is a sale against cash — Repo / Reverse Repo offer Cash only.
  // Amending a trade booked otherwise keeps its own option, or the disabled
  // select would render blank.
  const isRepoTrade = tradeType === 'repo' || tradeType === 'reverse_repo';
  const offersCollateral = (type: CollateralType) =>
    type === 'cash' || !isRepoTrade || (!!amendMode && collateralType === type);
  return (
    <div className="p-6 grid grid-cols-4 gap-x-4 gap-y-4">
      <div>
        <form.Field
          name="trade_type"
          validators={{
            onChange: ({ value }) => {
              if (!value) {
                return 'Trade Type is required';
              }
              return undefined;
            },
          }}
        >
          {(field) => (
            <>
              <label className={labelClassName}>
                Trade Type <span className="text-red-500">*</span>
              </label>
              <select
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value as TradeType)}
                disabled={inputsDisabled || isAmendLocked(amendMode, 'trade_type')}
                className={clsx(inputClassName, inputsDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed')}
              >
                <option value="">Select Trade Type</option>
                {availableTradeTypes.map((type) => (
                  <option key={type} value={type}>
                    {tradeTypeLabels[type] || type.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
              {isSubmitted && field.state.meta.errors.length > 0 && (
                <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
              )}
            </>
          )}
        </form.Field>
      </div>
      <div>
        <form.Field
          name="collateral_type"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Collateral Type is required';
              return undefined;
            },
          }}
        >
          {(field) => {
            const collateralValueMap: Record<string, CollateralType> = {
              'Cash': 'cash',
              'Non-cash': 'non_cash',
              'Uncollateralized': 'uncollateralized',
            };
            const reverseMap: Record<CollateralType, string> = {
              'cash': 'Cash',
              'non_cash': 'Non-cash',
              'uncollateralized': 'Uncollateralized',
            };
            const displayValue = field.state.value ? reverseMap[field.state.value] || field.state.value : 'Cash';
            return (
              <>
                <label className={labelClassName}>
                  Collateral Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={displayValue}
                  onChange={(e) => {
                    const selectedDisplay = e.target.value;
                    const internalValue = collateralValueMap[selectedDisplay] || selectedDisplay as CollateralType;
                    field.handleChange(internalValue);
                  }}
                  disabled={inputsDisabled || isAmendLocked(amendMode, 'collateral_type')}
                  className={clsx(inputClassName, inputsDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed')}
                >
                  <option value="Cash">Cash</option>
                  {offersCollateral('non_cash') && <option value="Non-cash">Non-cash</option>}
                  {offersCollateral('uncollateralized') && (
                    <option value="Uncollateralized">Uncollateralized</option>
                  )}
                </select>
                {isSubmitted && field.state.meta.errors.length > 0 && (
                  <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
                )}
              </>
            );
          }}
        </form.Field>
      </div>
      {/* LR-586: cash collateral names the pool that holds it. Sits next to the
          collateral type it depends on; hidden for the other two types, which
          hold no pool cash.
          LR-631: and hidden in GMSLA Rebate, which pools nothing at all - "поле
          кэшпула вообще можно убирать" (customer, 2026-08-24). A fee trade
          always carries one; GMRA is unchanged.
          Hidden in amend as well: the pool is not amendable (moving a trade
          between pools needs offsetting postings, out of scope), and the amend
          form carries no counterparty_id to filter the dropdown by - it would
          render as a permanently empty, disabled control. The booked pool is on
          the Trade Terms tab. */}
      {collateralType === 'cash' && showsPool && !amendMode && (
        <div>
          <CashPoolField form={form} isSubmitted={isSubmitted} disabled={inputsDisabled} />
        </div>
      )}
      <div>
        <form.Field
          name="quantity"
          validators={{
            onChange: ({ value }) => {
              if (!value || value <= 0) return 'Quantity must be greater than 0';
              return undefined;
            },
          }}
        >
          {(field) => {
            const availableQty = selectedInventoryItem?.quantity || 0;
            // LR-484: the "exceeds available inventory" check is a create-time concept
            // (booking against inventory). In amend there is no inventory pick, so the
            // synthetic item's qty would spuriously trip it — suppress in amend mode.
            const hasQuantityWarning =
              !amendMode && !!field.state.value && field.state.value > availableQty;
            return (
              <>
                <label className={labelClassName}>
                  Quantity <span className="text-red-500">*</span>
                </label>
                <DecimalInput
                  value={field.state.value || undefined}
                  onChange={(v) => field.handleChange(v ?? 0)}
                  precision={0}
                  onFocus={(e) => e.target.select()}
                  disabled={inputsDisabled || isAmendLocked(amendMode, 'quantity')}
                  className={clsx(
                    inputClassName,
                    isSubmitted && field.state.meta.errors.length > 0 && 'border-red-500',
                    hasQuantityWarning && field.state.meta.errors.length === 0 && 'border-yellow-500',
                    inputsDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed'
                  )}
                />
                {isSubmitted && field.state.meta.errors.length > 0 && (
                  <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
                )}
                {hasQuantityWarning && field.state.meta.errors.length === 0 && (
                  <p className="mt-1 text-xs text-yellow-600">
                    Quantity exceeds available inventory ({availableQty.toLocaleString()})
                  </p>
                )}
              </>
            );
          }}
        </form.Field>
      </div>
      {/* "Available" is the create-time inventory-availability readout. In amend there
          is no inventory pick (the synthetic item carries the booked qty), so the figure
          is meaningless and reads as a confusing "0" — hide it in amend mode. */}
      {!amendMode && (
        <div>
          <label className={labelClassName}>
            Available
          </label>
          <input
            type="text"
            value={(selectedInventoryItem?.quantity || 0).toLocaleString()}
            readOnly
            className={readOnlyInputClassName}
          />
        </div>
      )}
      <div>
        <form.Field name="margin_bps">
          {(field) => (
            <MarginInputInner
              field={field as unknown as MarginInputInnerProps['field']}
              selectedInventoryItem={selectedInventoryItem}
              amendMode={amendMode}
            />
          )}
        </form.Field>
      </div>
      <div>
        <form.Field
          name="gross_price"
          validators={{
            onChange: ({ value }) => {
              const numValue = typeof value === 'string' ? parseFloat(value) : value;
              if (!numValue || numValue <= 0) {
                return `${amendMode ? GROSS_PRICE_LABEL_AMEND : GROSS_PRICE_LABEL_CREATE} is required`;
              }
              return undefined;
            },
          }}
        >
          {(field) => {
            const numValue =
              typeof field.state.value === 'number'
                ? field.state.value
                : field.state.value
                  ? parseFloat(field.state.value)
                  : undefined;
            return (
              <>
                <label className={labelClassName}>
                  {amendMode ? GROSS_PRICE_LABEL_AMEND : GROSS_PRICE_LABEL_CREATE} <span className="text-red-500">*</span>
                </label>
                <DecimalInput
                  value={Number.isFinite(numValue) ? (numValue as number) : undefined}
                  onChange={(v) => field.handleChange(v === undefined ? '' : String(v))}
                  precision={2}
                  disabled={inputsDisabled || isAmendLocked(amendMode, 'gross_price')}
                  className={clsx(
                    inputClassName,
                    isSubmitted && field.state.meta.errors.length > 0 && 'border-red-500',
                    inputsDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed',
                  )}
                  title={amendMode ? GROSS_PRICE_TITLE_AMEND : GROSS_PRICE_TITLE_CREATE}
                  // LR-578 (review kvi #1): lets useCobGrossPricePrefill skip the
                  // async overwrite while the operator is inside this field.
                  data-field={GROSS_PRICE_FIELD_MARKER}
                />
                {isSubmitted && field.state.meta.errors.length > 0 && (
                  <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
                )}
              </>
            );
          }}
        </form.Field>
      </div>
      <div>
        <form.Field name="current_market_price">
          {(field) => {
            const numValue =
              typeof field.state.value === 'number'
                ? field.state.value
                : field.state.value
                  ? parseFloat(field.state.value)
                  : undefined;
            return (
              <>
                <label className={labelClassName}>Current Market Price</label>
                <DecimalInput
                  value={Number.isFinite(numValue) ? (numValue as number) : undefined}
                  onChange={(v) => field.handleChange(v === undefined ? '' : String(v))}
                  precision={2}
                  disabled={inputsDisabled || isAmendLocked(amendMode, 'current_market_price')}
                  placeholder="Optional"
                  className={clsx(
                    inputClassName,
                    inputsDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed',
                  )}
                  title="Current live market price (optional snapshot)"
                />
              </>
            );
          }}
        </form.Field>
      </div>
      <div>
        <label className={labelClassName}>
          Margin Price
        </label>
        <input
          type="text"
          value={calculatedValues.marginPrice ? calculatedValues.marginPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
          readOnly
          className={clsx(readOnlyInputClassName, 'text-gray-600 font-semibold')}
        />
      </div>
      <div>
        {/* LR-474: disambiguated from the trade's settlement currency. */}
        <label className={labelClassName}>
          Security Currency
        </label>
        <input
          type="text"
          value={displayCurrency || ''}
          readOnly
          className={readOnlyInputClassName}
        />
      </div>
      <div>
        <label className={labelClassName}>
          Contract Value ({displayCurrency || 'USD'})
        </label>
        <input
          type="text"
          value={calculatedValues.contractValue.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          readOnly
          className={clsx(readOnlyInputClassName, 'text-gray-900 font-semibold')}
        />
      </div>
    </div>
  );
};
